import {
  IntegrationType,
  PageAdsApi,
  PageAdsProps,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import { createRef, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { useFirstPluginConfig } from '#/plugins/hooks/useFirstPluginConfig';
import { getCcpaOptOutSetting } from '#/service/user/settings';
import { useBootStore } from '#/store/boot/boot.store';

import { pluginManager } from '../pluginManager';

interface PageAdsContainerProps {
  $isVisible: boolean;
}

const PageAdsContainer = styled.div<PageAdsContainerProps>`
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  z-index: 1;
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};

  /* make sure that at least the container won't block clicks */
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

// common props that any page ads plugin can use
interface CommonProps {
  routeFilter?: string;
}

export const PageAdsManager = () => {
  const { isPluginManagerReady } = useBootStore();

  const firstConfig = useFirstPluginConfig(IntegrationType.PAGE_ADS);
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  const ccpaOptOut = getCcpaOptOutSetting();

  // common props that applies to all plugins
  const { routeFilter = '' } = (firstConfig?.properties as CommonProps) || {};

  // regex to match routes where we should show ads
  const routeFilterRegex = useMemo(
    () => new RegExp(routeFilter, 'i'),
    [routeFilter]
  );

  // show/hide on route change using the configured routeFilter
  useEffect(() => {
    const match = !!routeFilterRegex.exec(pathname);
    setIsVisible(match);
  }, [pathname, routeFilterRegex]);

  // wrapped component rendered by the plugin
  const pageAdsComponent = useMemo(() => {
    if (!firstConfig || !isPluginManagerReady) {
      return null;
    }

    const PageAdsComponent = pluginManager.getPluginComponent<PageAdsProps>({
      type: IntegrationType.PAGE_ADS,
      id: firstConfig.key,
      optional: true,
    });
    const apiRef = createRef<PageAdsApi>();

    return (
      <ErrorBoundary key={firstConfig.key} onError={PageAdsComponent.onError}>
        <Suspense fallback={null}>
          <PageAdsComponent apiRef={apiRef} ccpaOptOut={ccpaOptOut} />
        </Suspense>
      </ErrorBoundary>
    );
  }, [ccpaOptOut, firstConfig, isPluginManagerReady]);

  if (!pageAdsComponent) {
    return null;
  }

  return (
    <PageAdsContainer $isVisible={isVisible} data-testid="pageAdsContainer">
      {pageAdsComponent}
    </PageAdsContainer>
  );
};
